import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import { addMoney, money, type Money } from '../money.ts';
import { CostLineSchema, costTotals, lineBuilderCost } from './cost-line.ts';

/**
 * A purchase order: what a builder has promised to pay a sub or a supplier.
 *
 * The reason it exists in Phase 5 is `committed` on the budget — money that
 * is spoken for but not yet billed. That is the column a job uses to answer
 * "can I still afford the tiles", and until purchase orders exist
 * `jobCosting` returns it as structurally zero with a flag saying so.
 *
 * `docs/features/08-purchase-orders-bills.md` §§1–2 is the screen. This
 * file is the shape and the rules.
 */

/**
 * Where a purchase order has got to.
 *
 * Four states, and the one worth explaining is the difference between
 * `released` and `approved`. Released means the sub has been shown it and
 * can work to it — that is the commitment. Approved is an internal sign-off
 * some companies require on top, which is why it is a separate state rather
 * than a replacement: a released PO is already money, and a product that
 * made the commitment wait for a second signature would have people
 * working off a PO the budget could not see.
 *
 * So **both released and approved count as committed**, and `isCommitted`
 * says so in one place rather than in every caller.
 */
export const PO_STATUSES = ['unreleased', 'released', 'approved', 'void'] as const;
export const PoStatusSchema = z.enum(PO_STATUSES);
export type PoStatus = z.infer<typeof PoStatusSchema>;

/**
 * The transitions this product allows, as data.
 *
 * `released → unreleased` is the `Unrelease` button in the frames, and it
 * is deliberately allowed: a PO released to the wrong sub, or with the
 * wrong scope, is a thing that happens on a Monday morning and the answer
 * cannot be "void it and raise another", because the number has been read
 * out to a supplier.
 *
 * Nothing leaves `void`. A voided PO stays in the list as a record that the
 * number was used, which is why there is no delete either.
 */
export const PO_TRANSITIONS: Readonly<Record<PoStatus, readonly PoStatus[]>> = {
  unreleased: ['released', 'void'],
  released: ['approved', 'unreleased', 'void'],
  approved: ['unreleased', 'void'],
  void: [],
};

export function canTransitionPo(from: PoStatus, to: PoStatus): boolean {
  return PO_TRANSITIONS[from].includes(to);
}

/** Whether this status means the money is promised. Released or approved. */
/**
 * What each status is called in front of a person.
 *
 * Here rather than in the page, because two surfaces now name these — the
 * list and the CSV export (P5-09d2) — and two copies is two chances for
 * one of them to say `unreleased`.
 */
export const PO_STATUS_LABEL: Record<PoStatus, string> = {
  unreleased: 'Unreleased',
  released: 'Released',
  approved: 'Approved',
  void: 'Void',
};

export function isCommitted(status: PoStatus): boolean {
  return status === 'released' || status === 'approved';
}

/**
 * Who may still change the scope and the lines. Only an unreleased one.
 *
 * A released PO is a promise somebody is working to, so editing it from the
 * client is refused and `Unrelease` is the way back. The rules say the same
 * thing, which is what makes it true.
 */
export function isPoEditable(status: PoStatus): boolean {
  return status === 'unreleased';
}

/**
 * Whether the sub has finished.
 *
 * Separate from the PO's own status, because they answer different
 * questions: the status is about the commitment, the work status is about
 * the site. A PO can be released and complete (work done, not yet billed)
 * or approved and incomplete.
 */
export const PO_WORK_STATUSES = ['notComplete', 'complete'] as const;
export const PoWorkStatusSchema = z.enum(PO_WORK_STATUSES);
export type PoWorkStatus = z.infer<typeof PoWorkStatusSchema>;

/**
 * When the work is due.
 *
 * Either a date somebody typed or a schedule item it follows — the frames'
 * `Choose Date | Link to Schedule Item`. Stored as the choice rather than
 * as a resolved date, so a PO linked to an activity **moves when the
 * activity moves**. A resolved copy would be right on the day it was
 * written and wrong by the time the sub read it, which is the bug P4-19
 * raises for selection deadlines.
 */
export const PoCompletionSchema = z.union([
  z.object({ kind: z.literal('date'), date: DateOnlySchema }),
  z.object({ kind: z.literal('scheduleItem'), scheduleItemId: IdSchema }),
]);
export type PoCompletion = z.infer<typeof PoCompletionSchema>;

/**
 * **There is no `internalNotes` field, and that is the point.**
 *
 * `docs/features/08-purchase-orders-bills.md` §2 puts Internal Notes on
 * this record under an "Internal Only" heading. It cannot live here: the
 * sub this purchase order is for reads **this document**, and Firestore
 * rules are all-or-nothing per document — a field a renderer leaves out is
 * still a field in the bytes the browser received. Leaving it out of a
 * component is not a confidentiality boundary, and this product has said
 * so twice already (`ownerChangeOrders`, `ownerBudgetLines`).
 *
 * So the field is not stored at all until it can be stored somewhere a sub
 * cannot read — a sibling document under the purchase order, or a mirror
 * the sub reads instead of this one. P5-09e. An earlier draft of this file
 * had the field with the comment "never shown to the sub", which was true
 * of the component and false of the document.
 */
export const PurchaseOrderSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /**
   * `SFM-0001`: the job's prefix and a per-job sequence.
   *
   * Unlike a change order's bare `0001`, because a PO number is read out to
   * a supplier who works for several builders on several jobs, and
   * `docs/DATA_MODEL.md` §6 has it this way. The sequence is zero-padded to
   * four for the same reason change orders are: the list orders by the
   * number **string**, and `'12'` sorts after `'0100'`.
   */
  number: z.string().trim().min(1).max(32),
  title: z.string().trim().min(1).max(200),
  /** The sub or supplier. Optional while somebody is still drafting it. */
  assignedContactId: IdSchema.optional(),
  /** No labour: changes nothing here, and tells the sub what they are for. */
  materialsOnly: z.boolean().default(false),
  /** What was agreed, in the words the sub reads. */
  scope: z.string().max(20_000).default(''),
  status: PoStatusSchema.default('unreleased'),
  workStatus: PoWorkStatusSchema.default('notComplete'),
  /** Stamped when the work is marked complete, and **cleared when it is not**. */
  completedAt: InstantSchema.optional(),
  scheduledCompletion: PoCompletionSchema.optional(),
  releasedAt: InstantSchema.optional(),
  lines: z.array(CostLineSchema).default([]),
  /**
   * The package this order came out of, when it came out of one.
   *
   * `relatedBidIds` alone cannot be resolved to documents — a bid lives
   * under its package — so the two travel together or neither is
   * readable.
   */
  sourceBidPackageId: IdSchema.optional(),
  /**
   * Provenance when it came from a bid (P8-10d).
   *
   * **Every live bid on the package, not only the one that won.** The
   * question a builder asks six months later is not "who got it" — the
   * order says that — but "what else was quoted, and was this a good
   * price". Filled by the server from the package rather than by the
   * client, so an order cannot claim provenance it does not have.
   */
  relatedBidIds: z.array(IdSchema).default([]),
  tagIds: z.array(IdSchema).default([]),
});
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

/**
 * What a purchase order commits, and where.
 *
 * The **cost** only. A purchase order has no owner price: it is what the
 * builder pays, and the markup on it belongs to the estimate or the change
 * order that sold the work. `costTotals` would hand back a price, and a
 * page that printed one would be inventing a second answer to "what does
 * the owner pay" — so this returns cost and nothing else.
 */
export function poCost(po: Pick<PurchaseOrder, 'lines'>, currency = 'USD'): Money {
  return costTotals(po.lines, currency).cost;
}

/**
 * Committed cost per cost code, from a job's purchase orders.
 *
 * Only released and approved ones: an unreleased PO is a draft nobody has
 * promised anything with, and counting it would let a job look committed
 * to work that has not been ordered. A voided one is not money either.
 *
 * **Less what has been billed against it** is the other half of the
 * definition in `docs/features/07-costing-budget.md` §7, and it is not here
 * yet: bills are P5-10, so nothing has been billed and the subtraction is
 * zero. When bills arrive this takes them as a second argument rather than
 * changing shape — the committed figure is "promised but not yet invoiced
 * by the sub", so a fully billed PO commits nothing and its money has
 * moved to `actual`.
 *
 * A line with no cost code is counted under `null`, like every other
 * uncoded money in `jobCosting`, so the budget page can name it rather
 * than dropping it.
 */
export function committedByCostCode(
  purchaseOrders: readonly PurchaseOrder[],
  currency = 'USD',
): Map<string | null, Money> {
  const byCode = new Map<string | null, Money>();
  for (const po of purchaseOrders) {
    if (!isCommitted(po.status)) continue;
    for (const line of po.lines) {
      const key = line.costCodeId ?? null;
      byCode.set(key, addMoney(byCode.get(key) ?? money(0, currency), lineBuilderCost(line)));
    }
  }
  return byCode;
}

/** The next PO number for a job, formatted the way it is read out. */
export function formatPoNumber(prefix: string, sequence: number): string {
  return `${prefix.trim().toUpperCase()}-${String(sequence).padStart(4, '0')}`;
}

/** The widest sequence `formatPoNumber` renders without growing. */
export const PO_NUMBER_MAX = 9999;

/**
 * The sequence out of a PO number, or `undefined`.
 *
 * Takes the digits after the **last** hyphen, because a job prefix may
 * carry one — `NE-PHX` is a prefix somebody will type — and the sequence is
 * always last. Refuses anything that is not four digits, for the ordering
 * reason on `number` above.
 */
export function parsePoNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  const tail = trimmed.slice(trimmed.lastIndexOf('-') + 1);
  if (!/^\d{1,4}$/.test(tail)) return undefined;
  const sequence = Number.parseInt(tail, 10);
  return sequence >= 1 && sequence <= PO_NUMBER_MAX ? sequence : undefined;
}

/**
 * Whether a sub may see this purchase order at all.
 *
 * Released or approved, and **theirs**. A PO carries the builder's cost for
 * work by somebody else, so a sub reading another sub's PO learns what a
 * competitor charges. The rules enforce it by comparing the membership's
 * own `contactId` to `assignedContactId`; this is the same answer for the
 * UI, and the two are asserted together in `firebase/rules.test.ts`.
 */
export function visibleToSub(
  po: Pick<PurchaseOrder, 'status' | 'assignedContactId'>,
  contactId: string | undefined,
): boolean {
  if (!contactId) return false;
  return isCommitted(po.status) && po.assignedContactId === contactId;
}

/** An empty purchase order, for a form that has not been saved yet. */
export function emptyPurchaseOrder(
  id: string,
  context: { readonly companyId: string; readonly jobId: string; readonly number: string },
): Omit<PurchaseOrder, 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'> {
  return {
    id,
    companyId: context.companyId,
    jobId: context.jobId,
    number: context.number,
    title: '',
    materialsOnly: false,
    scope: '',
    status: 'unreleased',
    workStatus: 'notComplete',
    lines: [],
    relatedBidIds: [],
    tagIds: [],
  };
}
